// Who actually ran each phase.
//
// The phase table on a project showed the machine and never the person. The data
// was there all along — every finished drum writes an ExecutionLog, and that log
// carries `operators[]`, one span per person who was at the machine during it,
// with the metres each of them made.
//
// The plural matters and is the reason this is not a one-liner. A phase runs for
// hours and crosses shift changes: a drum started by one operator and finished by
// another has two spans, and a phase with six drums across a night can easily
// have three people on it. Showing only `log.operator` — the dominant one — would
// name one person for work three did, which is worse than naming nobody.

/** Sum, per person, across every drum of a task. */
export function operatorsByTask(logs = []) {
  const byTask = new Map();

  for (const log of Array.isArray(logs) ? logs : []) {
    const taskId = log?.taskId;
    if (!taskId) continue;

    const entry = byTask.get(taskId) || {
      people: new Map(),
      drums: 0,
      unattributed: 0,
    };
    entry.drums += 1;

    // The spans are the truth. `operator` is only the dominant one, kept for
    // logs written before per-operator spans existed — and for those it is all
    // there is, so it stands in for a single span covering the whole drum.
    const spans =
      Array.isArray(log.operators) && log.operators.length
        ? log.operators
        : log.operator
          ? [{ operator: log.operator, meters: log.producedLength, runningSec: log.durationSec }]
          : [];

    // One drum can hold several spans from the SAME person — they stepped away
    // and came back, which the tracker records as two. Counting spans would
    // report that drum twice.
    const countedHere = new Set();

    for (const span of spans) {
      const name = span?.operator;
      if (!name) {
        // Metres produced with nobody signed in at the panel. Kept rather than
        // dropped: silently excluding them makes whoever WAS there look like
        // they made 100% of a drum they only partly made.
        entry.unattributed += Number(span?.meters) || 0;
        continue;
      }
      const person = entry.people.get(name) || {
        operator: name,
        meters: 0,
        runningSec: 0,
        drums: 0,
      };
      person.meters += Number(span.meters) || 0;
      person.runningSec += Number(span.runningSec) || 0;
      if (!countedHere.has(name)) {
        person.drums += 1;
        countedHere.add(name);
      }
      entry.people.set(name, person);
    }

    byTask.set(taskId, entry);
  }

  const out = {};
  for (const [taskId, entry] of byTask) {
    const people = [...entry.people.values()].sort((a, b) => b.meters - a.meters);
    // The share is of everything produced, including what nobody was there for,
    // so two operators on a drum with an unmanned stretch do not add up to 100%.
    const total = people.reduce((s, p) => s + p.meters, 0) + entry.unattributed;
    out[taskId] = {
      drums: entry.drums,
      unattributed: entry.unattributed,
      // Most metres first: the eye should land on whoever did most of the work.
      operators: people.map(p => ({
        ...p,
        share: total > 0 ? p.meters / total : 0,
      })),
    };
  }
  return out;
}

/** "Virgil Muresan" · "Virgil Muresan +1" — what fits in a table cell. */
export function summariseOperators(entry) {
  const people = entry?.operators || [];
  if (!people.length) return null;
  if (people.length === 1) return people[0].operator;
  return `${people[0].operator} +${people.length - 1}`;
}
